import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

const STORAGE_KEY = 'agri.theme'

export const THEMES = ['clair', 'sombre'] as const

export type Theme = (typeof THEMES)[number]

interface ThemeValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeValue | null>(null)

function isTheme(value: string | null): value is Theme {
  return value !== null && (THEMES as readonly string[]).includes(value)
}

/** Thème initial : le choix déjà fait, sinon la préférence du système, sinon clair. */
function detectTheme(): Theme {
  const stored = window.localStorage.getItem(STORAGE_KEY)

  if (isTheme(stored)) {
    return stored
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'sombre' : 'clair'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(detectTheme)

  // Comme la direction d'écriture, le thème est porté par <html> : un seul attribut
  // suffit, et aucun composant n'a besoin de savoir lequel est actif.
  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, next)
    setThemeState(next)
  }, [])

  const value = useMemo<ThemeValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme: () => setTheme(theme === 'clair' ? 'sombre' : 'clair'),
    }),
    [theme, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  const context = useContext(ThemeContext)

  if (context === null) {
    throw new Error('useTheme doit être appelé à l’intérieur de <ThemeProvider>.')
  }

  return context
}
